'use client';

import { Check, Clock, ExternalLink, Lock } from 'lucide-react';
import { Avatar } from './Avatar';
import type { EventShift, GatheringDoor, PersonRegistration, PromoLine } from '@/lib/types';
import { fmtClock, fmtInstantDay, fmtMoney } from '@/lib/format';
import { showsBand } from '@/lib/shift-screen';

/**
 * WHAT THIS PERSON IS COMING AS
 * =============================
 *
 * One card per registration, for one person at a time. The label, and what
 * THEY will pay: a discount they qualify for is applied here, with the full
 * price struck through beside it, because a price that changes silently two
 * screens later reads as a mistake. A registration that costs nothing says
 * nothing — "Free" is for a price that was brought down to zero, not for a
 * night that never had one.
 *
 * No descriptions. Three cards of sentences is a wall to read where the
 * question is which one is you, and the answer is in the label.
 *
 * A registration they could unlock is shown and disabled with the reason on
 * it: somebody who cannot volunteer yet needs to know why and what to do.
 * One that was never theirs is not shown at all. See `offersFor`.
 *
 * Picking one moves on by itself. The choice is the whole question on this
 * screen, and a Continue button under a screen with one answer on it is a
 * second click for nothing.
 *
 * The volunteer card previews the work as pills — the roles, not the times —
 * and picking it opens the shifts on their own screen. A shift IS the
 * registration for a volunteer, so it is asked straight away, but asking it
 * underneath the card pushed the other ways in a screen's worth of
 * checkboxes down the page.
 */

export interface RegistrationTypesProps {
  person: {
    firstName: string;
    lastName?: string;
    image?: string | null;
    cppCurrent: boolean;
    registrations?: PersonRegistration[];
  };
  /** Which of the party this is, when there is more than one. */
  index?: number;
  count?: number;
  doors: GatheringDoor[];
  shifts: EventShift[];
  chosenDoorId: number | null;
  onChoose: (door: GatheringDoor) => void;
  /** This person's automatic discounts, as the server decided them. */
  promos: PromoLine[];
}

/**
 * WHICH REGISTRATIONS TO PUT IN FRONT OF SOMEBODY
 * ===============================================
 *
 * Three answers, not two:
 *
 *   open    they may pick it
 *   locked  it is theirs to unlock, so it is shown with the reason on it: a
 *           volunteer without a current CPP approval needs to know that, and
 *           what to do about it
 *   hidden  it was never theirs. Somebody who is not student ministry staff
 *           is not offered a staff registration, because a door that cannot
 *           open is noise dressed as a choice
 *
 * The server decides, because the facts are MP's: who is on staff, who leads
 * a small group, whose background check is current. `registrations` absent
 * means an event that never had more than one way in, and everything is open.
 */
export interface Offer {
  open: GatheringDoor[];
  /** `when` marks the two that are only a matter of time. */
  locked: { door: GatheringDoor; unmet: string; url?: string | null; when?: 'early' | 'over' }[];
}

/**
 * WHEN A REGISTRATION IS TAKING SIGN-UPS
 * ======================================
 *
 * Its own window when it has one, because an event with several ways in does
 * not have one date: volunteers are asked weeks before students hear about
 * the night, and leader sign-ups close early so the groups can be built while
 * the student list is still open. Null on either side defers to the event.
 */
export function windowOf(door: GatheringDoor, now = Date.now()): 'open' | 'early' | 'over' {
  if (door.opensAt && Date.parse(door.opensAt) > now) return 'early';
  if (door.closesAt && Date.parse(door.closesAt) <= now) return 'over';
  return 'open';
}

/** What a closed registration says instead of a price. */
export function windowNote(door: GatheringDoor, when: 'early' | 'over'): string {
  return when === 'early'
    ? `Opens ${fmtInstantDay(door.opensAt)}`
    : `Closed ${fmtInstantDay(door.closesAt)}`;
}

export function offersFor(
  doors: GatheringDoor[],
  person: { cppCurrent?: boolean; registrations?: PersonRegistration[] }
): Offer {
  const offer: Offer = { open: [], locked: [] };
  for (const door of doors) {
    const said = person.registrations?.find((r) => r.doorId === door.id);
    // Whose it is comes first. A registration that was never theirs stays
    // hidden whatever its dates say — a student does not need to know when
    // the staff door opens.
    if (said && !said.ok && !said.fixable) continue;

    // Then the clock, which outranks anything they could fix: a volunteer
    // whose CPP has lapsed does not need to hear about it three weeks before
    // sign-ups open, and there is nothing to fix about a date.
    const when = windowOf(door);
    if (when !== 'open') {
      offer.locked.push({ door, unmet: windowNote(door, when), when });
      continue;
    }

    if (said) {
      if (said.ok) offer.open.push(door);
      else {
        const rule = door.requirements[0];
        offer.locked.push({
          door,
          unmet: said.unmet ?? rule?.unmet ?? '',
          url: rule?.url ?? null,
        });
      }
      continue;
    }
    // Nothing said about this person: fall back to the one rule the page can
    // answer itself.
    const cpp = door.requirements.find((r) => r.key === 'cpp_current');
    if (cpp && !person.cppCurrent)
      offer.locked.push({ door, unmet: cpp.unmet, url: cpp.url ?? null });
    else offer.open.push(door);
  }
  return offer;
}

/** The shifts all start "Flannel Fest | ". Say it once, in the band heading. */
export function trimShared(titles: string[]): (title: string) => string {
  if (titles.length < 2) return (t) => t;
  for (const separator of [' | ', ' - ', ': ']) {
    const heads = titles.map((t) => t.split(separator)[0]);
    const head = heads[0];
    if (head !== undefined && heads.every((h) => h === head) && head !== titles[0]) {
      const cut = head.length + separator.length;
      return (t) => (t.startsWith(head + separator) ? t.slice(cut) : t);
    }
  }
  return (t) => t;
}

/**
 * What there is to do, without the times: the same job runs in three bands,
 * and the pills on the card preview the work rather than the schedule.
 */
export function shiftRoles(shifts: EventShift[]): string[] {
  const shorten = trimShared(shifts.map((s) => s.title));
  const roles: string[] = [];
  for (const shift of shifts) {
    const role = shorten(shift.title).trim();
    if (role && !roles.includes(role)) roles.push(role);
  }
  return roles;
}

function band(start: string | null, end: string | null): string {
  if (!start) return 'Any time';
  return end ? `${fmtClock(start)} to ${fmtClock(end)}` : `From ${fmtClock(start)}`;
}

/**
 * THE SHIFTS, ON THEIR OWN SCREEN
 * ===============================
 *
 * Reached by picking the volunteer registration, and the only thing on it, so
 * the times get the room they need. Grouped by the hours they run, because
 * the first question a volunteer has is when, and the second is what.
 */
export function ShiftPicker({
  person,
  shifts,
  chosen,
  onToggle,
}: {
  person: { firstName: string; lastName?: string; image?: string | null };
  shifts: EventShift[];
  chosen: number[];
  onToggle: (shiftId: number) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Avatar
          name={`${person.firstName} ${person.lastName ?? ''}`.trim()}
          image={person.image ?? null}
          size={40}
        />
        <p className="text-navy text-base font-bold">What is {person.firstName} helping with?</p>
      </div>
      <Shifts shifts={shifts} chosen={chosen} onToggle={onToggle} />
    </div>
  );
}

export function Shifts({
  shifts,
  chosen,
  onToggle,
}: {
  shifts: EventShift[];
  chosen: number[];
  onToggle: (id: number) => void;
}) {
  const shorten = trimShared(shifts.map((s) => s.title));
  const bands = new Map<string, EventShift[]>();
  for (const shift of shifts) {
    const k = `${shift.start ?? ''}|${shift.end ?? ''}`;
    bands.set(k, [...(bands.get(k) ?? []), shift]);
  }
  return (
    <div className="space-y-4">
      {[...bands.entries()].map(([key, group]) => {
        // A band exists because a shift was pushed into it, so there is always
        // a first one to read the times off.
        const first = group[0];
        if (!first) return null;
        return (
          <div key={key}>
            {showsBand(bands.size, first.start) && (
              <div className="kicker mb-1.5">{band(first.start, first.end)}</div>
            )}
            <div className="grid gap-1.5">
              {group.map((shift) => {
                const on = chosen.includes(shift.id);
                const clash = !on && overlapsChosen(shift, shifts, chosen);
                const off = shift.full || clash;
                return (
                  <button
                    key={shift.id}
                    type="button"
                    disabled={off}
                    onClick={() => onToggle(shift.id)}
                    /**
                     * THE TRANSITION NAMES ITS PROPERTIES, AND OPACITY IS NOT
                     * ONE OF THEM.
                     *
                     * A shift that clashes with one already chosen goes faded
                     * and its label changes from "4 left" to "Overlaps" in the
                     * same render. Tailwind's bare `transition` animates
                     * opacity, which puts the row on its own compositing layer
                     * for the duration — and iOS Safari does not always repaint
                     * the text inside a layer it is fading. Both strings ended
                     * up on screen at once, drawn over each other, which is
                     * exactly what it looked like on a phone.
                     *
                     * Colour, border and shadow still animate. The fade is
                     * instant, which nobody will notice, and the row is never
                     * promoted mid-text-change.
                     */
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-[border-color,background-color,box-shadow] ${
                      on
                        ? 'border-teal bg-teal-soft'
                        : off
                          ? 'border-line bg-white opacity-55'
                          : 'border-line bg-white hover:shadow-md'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                        on ? 'bg-teal border-teal text-white' : 'border-line'
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <span className="text-navy min-w-0 flex-1 text-sm font-semibold">
                      {shorten(shift.title)}
                    </span>
                    <ShiftStatus shift={shift} clash={clash} />
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * What this shift says on its right-hand side: how many are left, or why it
 * cannot be picked.
 *
 * Its own component with the text as its `key`, so React replaces the node
 * when the answer changes rather than rewriting the characters inside one
 * that is already on screen. Mutating text in place is the other half of the
 * ghosting on iOS; a new node cannot inherit a stale paint from the old one.
 */
function ShiftStatus({ shift, clash }: { shift: EventShift; clash: boolean }) {
  const label = shift.full
    ? 'Full'
    : clash
      ? 'Overlaps'
      : shift.remaining == null
        ? 'Open'
        : `${shift.remaining} left`;
  return (
    <span
      key={label}
      className={`shrink-0 text-xs font-bold ${
        shift.full || clash ? 'text-slate' : 'text-teal-deep'
      }`}
    >
      {label}
    </span>
  );
}

/** Two shifts clash when their clock ranges cross. */
function overlapsChosen(shift: EventShift, all: EventShift[], chosen: number[]): boolean {
  const mine = range(shift);
  if (!mine) return false;
  return chosen.some((id) => {
    const other = all.find((s) => s.id === id);
    const theirs = other ? range(other) : null;
    return theirs ? mine[0] < theirs[1] && theirs[0] < mine[1] : false;
  });
}

function range(shift: EventShift): [number, number] | null {
  const mins = (v: string | null) => {
    if (!v) return null;
    const m = /^(\d{1,2}):(\d{2})/.exec(v);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const start = mins(shift.start);
  const end = mins(shift.end);
  return start == null || end == null ? null : [start, end];
}

export function RegistrationTypes({
  person,
  index = 0,
  count = 1,
  doors,
  shifts,
  chosenDoorId,
  onChoose,
  promos,
}: RegistrationTypesProps) {
  // The discounts the server decided for this person come off the priced
  // registration. With one priced registration that is exact; if a second one
  // ever has a price, the server's own line wins at review either way.
  const off = promos.reduce((sum, p) => sum + p.price, 0);
  const offer = offersFor(doors, person);
  const roles = shiftRoles(shifts);
  const shown = [
    ...offer.open.map((door) => ({
      door,
      unmet: null as string | null,
      url: null as string | null,
      when: undefined as 'early' | 'over' | undefined,
    })),
    ...offer.locked.map(({ door, unmet, url, when }) => ({ door, unmet, url, when })),
  ];

  return (
    <div className="space-y-2.5">
      {/* Whose answer this is, with their face on it: three people in a row
          of identical forms is how somebody answers for the wrong child. */}
      <div className="flex items-center gap-3 pb-1">
        <Avatar
          name={`${person.firstName} ${person.lastName ?? ''}`.trim()}
          image={person.image ?? null}
          size={40}
        />
        <div className="min-w-0 flex-1">
          <p className="text-navy text-base font-bold">How is {person.firstName} coming?</p>
          {count > 1 && (
            <p className="text-slate text-xs">
              {index + 1} of {count}
            </p>
          )}
        </div>
      </div>
      {/* EVERY CARD THE SAME HEIGHT. One has a price and a discount under it,
          one is a word on its own, one carries a row of shift pills — stacked
          at their natural heights they read as three different kinds of
          thing. `auto-rows-fr` makes every row as tall as the tallest, with
          no magic number to keep in step with the copy. */}
      <div className="grid auto-rows-fr gap-2.5">
        {shown.map(({ door, unmet: blocked, url: fixAt, when }) => {
          const on = chosenDoorId === door.id;
          const discounted = door.price > 0 && door.discountable && off < 0;
          const net = Math.max(0, door.price + (discounted ? off : 0));
          // A price only when there is one to say. Nothing where the night was
          // always free; "Free" only where a discount took it there.
          const money = net > 0 ? fmtMoney(net) : discounted ? 'Free' : null;
          const shell = `card flex h-full w-full items-center gap-3 p-4 text-left transition ${
            on
              ? '!border-teal !bg-teal-soft !ring-teal !ring-2'
              : blocked
                ? 'opacity-70'
                : 'lift hover:border-teal/40'
          }`;
          // A locked card is not a button — there is nothing to press, and the
          // link it carries has to be usable, which it never is inside a
          // disabled button.
          const Card = blocked ? 'div' : 'button';
          const card = (
            <Card
              {...(blocked ? {} : { type: 'button' as const, onClick: () => onChoose(door) })}
              className={shell}
            >
              {/* Chosen is a filled check, not a highlight: the card is about to
                slide away to the next person, and the tick is what says the
                tap landed. */}
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition ${
                  on ? 'bg-teal border-teal text-white' : 'border-line bg-white'
                }`}
              >
                {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-navy block text-base font-bold">{door.label}</span>
                {blocked && (
                  <span className="text-slate mt-1 flex items-start gap-1.5 text-xs">
                    {when ? (
                      <Clock className="mt-0.5 h-3 w-3 shrink-0" />
                    ) : (
                      <Lock className="mt-0.5 h-3 w-3 shrink-0" />
                    )}
                    <span>
                      {blocked}
                      {/* A rule somebody can act on says where. Most rules are
                        not that kind — "Women only" is not an errand — and
                        those carry no address and no link. */}
                      {fixAt && (
                        <a
                          href={fixAt}
                          target="_blank"
                          rel="noopener"
                          className="text-teal-deep ml-1.5 inline-flex items-center gap-1 font-bold whitespace-nowrap hover:underline"
                        >
                          Get approved <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </span>
                  </span>
                )}
                {!blocked && door.takesShifts && roles.length > 0 && (
                  <span className="mt-1.5 flex flex-wrap gap-1">
                    {roles.slice(0, 4).map((role) => (
                      <span
                        key={role}
                        className="bg-mist text-slate-deep rounded-full px-2 py-0.5 text-[0.68rem] font-semibold"
                      >
                        {role}
                      </span>
                    ))}
                    {roles.length > 4 && (
                      <span className="text-slate px-1 py-0.5 text-[0.68rem] font-semibold">
                        +{roles.length - 4}
                      </span>
                    )}
                  </span>
                )}
              </span>
              {money && (
                <span className="shrink-0 text-right">
                  <span className="text-navy block text-xl font-extrabold">
                    {discounted && net > 0 && (
                      <s className="text-slate mr-1.5 text-sm font-normal">
                        {fmtMoney(door.price)}
                      </s>
                    )}
                    {money}
                  </span>
                  {/*
                   * EACH DISCOUNT SAYS WHAT IT TOOK OFF.
                   *
                   * Two names and one number is a sum somebody has to do in
                   * their head to check we did ours — and a staff member
                   * whose student lands on $1 should be able to see which $2
                   * was the staff one. One per line rather than run together:
                   * a second discount is exactly when the breakdown matters,
                   * and at this size two of them on one line wrap badly on a
                   * phone.
                   */}
                  {discounted &&
                    promos.map((p) => (
                      <span
                        key={p.code || p.title}
                        className="text-teal-deep block text-[0.7rem] font-bold"
                      >
                        {p.title} −{fmtMoney(Math.abs(p.price))}
                      </span>
                    ))}
                </span>
              )}
            </Card>
          );
          return <div key={door.id}>{card}</div>;
        })}
      </div>
    </div>
  );
}
